/**
 * Provider Keys — the customer's write-only key entry surface.
 *
 * Campaign `auteur-cloud-commercial-v1`, task `C_VAULT`.
 *
 * Three properties of this page are security controls, not styling choices,
 * and each is asserted by `tests/auteur_cloud/test_vault_operator_blindness.py`:
 *
 * 1. **The key is never rendered.** The only value this page ever shows for a
 *    stored key is `masked_hint` (a public prefix, an ellipsis, four trailing
 *    characters) and `validation_status`. There is no reveal control, no "copy
 *    key" button, and no state variable holding a stored key — because the
 *    server has no endpoint that would return one (threat model §3.9).
 *
 * 2. **The entry field is write-only and short-lived.** `type="password"`,
 *    `autoComplete="off"`, and the draft is cleared from component state the
 *    moment a submit resolves *or* rejects. It is never written to
 *    `localStorage`, `sessionStorage`, a cookie, a URL, or a form that a
 *    browser would offer to remember.
 *
 * 3. **The backup exclusion is disclosed at the point of entry.**
 *    `PRIVACY_AND_RETENTION_V1.md` rule EX-2 requires exactly this, in those
 *    words: the exclusion is disclosed "at the point of key entry, not only in
 *    a contract", and the consequence is concrete — a restored backup restores
 *    the tenant *without* its provider keys. `VAULT_EXCLUSION_NOTICE` below is
 *    that disclosure and it renders beside the input, not behind a link.
 *
 * Each stored key also carries "Allow my workspace to use this key for chat and
 * generation" (relay contract K-12.3) when the organization has a hosted workspace:
 * the workspace never holds the key, and the relay spends it only once this switch is
 * on. The switch sends a key id, never key material, and turning it off stops the
 * next call (it is the one approval, so it covers chat and generation alike). The
 * Add-a-key form offers the same permission pre-checked (the customer's visible
 * choice, default on): after the key is stored the page allows it for each workspace
 * with the existing approval route; if that second step fails the key stays stored,
 * the switch stays off and the notice says so. A replaced key keeps its approval:
 * the key id is stable and the approval is keyed by it.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA. The
 * repo-wide JSX↔JS parity contract governs `static/`, not this Vite source.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  deleteProviderKey,
  listProviderKeys,
  replaceProviderKey,
  storeProviderKey,
  validateProviderKey,
} from './providerKeysApi';
import { HelpTip } from '../../components/HelpTip';
import { ProviderSetupGuide } from '../../components/Settings/ProviderSetupGuide';
import { GUIDE_TEXT, guideFor } from '../../components/Settings/providerSetupGuides';
import {
  RECOMMENDED_PROVIDER,
  groupLight,
  checkAge,
  coverageLine,
  matchesProviderSearch,
  isPlannedFor,
  keyLight,
  orderedCapabilities,
  providersIn,
  usedForWords,
} from '../../components/Settings/providerCapabilities';
import { ReturnToWorkspace } from '../../components/Settings/ReturnToWorkspace';
import { deepLinkId } from '../../lib/unlockDeepLink';
import { useSearch } from 'wouter';
import {
  MEDIA_LIMIT_MAX_UNITS,
  clearWorkspaceMediaLimit,
  listWorkspaceMediaLimits,
  readWorkspaceMediaUsage,
  grantWorkspaceMediaContinue,
  setWorkspaceMediaLimit,
  allowWorkspaceKey,
  listSpendApprovals,
  listWorkspaces,
  stopWorkspaceKey,
} from '../../api/workspaces';

/** Provider choices and labels come from the server catalog on listProviderKeys. */
/** Rule EX-2's disclosure. Shown at the point of entry; see the file comment. */
export const VAULT_EXCLUSION_NOTICE =
  'This key is encrypted per tenant and is never shown again — not to you, and not to ' +
  'anyone operating this service. It is excluded from customer exports and from ' +
  'application backups by contract, so a restored backup restores your account without ' +
  'your provider keys and you will re-enter them. Deleting a key destroys it immediately ' +
  'and there is no copy to recover.';

const STATUS_LABELS = {
  unvalidated: 'Not checked',
  valid: 'Valid',
  invalid: 'Rejected by provider',
  unreachable: 'Provider unreachable',
};

/**
 * F-67: the server derives `validation_freshness` from its own clock, so "recent" is trusted unless
 * the browser can positively show the check is over a day old. A page whose `now` lags the check
 * (it only ticks every 30 seconds) must not turn a check from a minute ago into "more than a day".
 */
export function recentlyValidated(record, now) {
  if (record.validation_freshness !== 'recent' || !record.validated_at) return false;
  return checkAge(record.validated_at, now) !== 'stale';
}

/** "Valid as of <time>" (audit IN-15): a positive check is always shown with when it happened. */
export function validAsOf(record) {
  const parsed = Date.parse(record.validated_at || '');
  if (!Number.isFinite(parsed)) return 'Valid';
  return `Valid as of ${new Date(parsed).toISOString().replace('T', ' ').slice(0, 16)} UTC`;
}

/**
 * What the server's readiness projection says this provider's keys are used for in Auteur Cloud,
 * in plain words (F-22): "Used for: writing and chat." Operations whose adapter is not built yet are
 * listed as coming later. Workspace permission is the per-workspace switch below it.
 */
export function KeyOperations({ readinessProvider }) {
  if (!readinessProvider) return null;
  const operations = readinessProvider.operations || [];
  const id = readinessProvider.id || '';
  const live = usedForWords(operations.filter((op) => op.adapter === 'implemented').map((op) => op.operation), id);
  const later = usedForWords(operations.filter((op) => op.adapter !== 'implemented').map((op) => op.operation), id);
  if (!live) {
    const planned = (readinessProvider.planned || [])[0];
    return (
      <p className="provider-key-operations">
        Used for: nothing in Auteur Cloud yet{later ? ` (${later} is coming later)` : planned ? ' (planned, coming later)' : ''}.
        {' '}Your key is stored safely and will be used once that arrives.
      </p>
    );
  }
  return (
    <p className="provider-key-operations">
      Used for: {live}.{later ? ` Coming later: ${later}.` : ''}
    </p>
  );
}

/** The workspace's short name for a switch label: the first label of its host. */
function workspaceName(workspace) {
  const first = String(workspace.ingress_host || '').split('.')[0];
  return first || 'workspace';
}

/** The permission's one-line wording, shared by the per-key switch and the Add-a-key checkbox. */
export const ALLOW_PERMISSION_TEXT = 'to use this key for chat and generation';

/** The result of "store the key, then allow it": what was asked and what happened. */
export function storeNotice(outcome) {
  const stored = 'Key stored. It is encrypted and cannot be displayed again.';
  if (!outcome || !outcome.hasWorkspace) return stored;
  if (outcome.failed > 0 || outcome.missingKeyId) {
    return `${stored} Your workspace could not be allowed to use it yet. Turn on "Allow my workspace ` +
      `${ALLOW_PERMISSION_TEXT}" beside the key to try again.`;
  }
  if (outcome.requested) {
    return `${stored} Your workspace can now use it for chat and generation. Turn it off any time beside the key.`;
  }
  return `${stored} Your workspace cannot use it until you turn on "Allow my workspace ${ALLOW_PERMISSION_TEXT}" beside the key.`;
}

/**
 * Store a key, then (when the customer left the box checked) allow it for each workspace.
 *
 * `store()` stores the key and returns the server's `{ provider_key }`; `allowKey(workspaceId, keyId)`
 * is the existing approval call. A store failure throws (nothing is allowed); an allow failure is counted
 * and never throws, so the key is never lost to a failed second step. Only the key id is passed on.
 */
export async function storeKeyAndAllow({ store, allowKey, workspaces, allow }) {
  const stored = await store();
  const keyId = stored && stored.provider_key && stored.provider_key.key_id;
  const outcome = {
    hasWorkspace: workspaces.length > 0,
    requested: false,
    allowed: 0,
    failed: 0,
    missingKeyId: false,
  };
  if (!allow || workspaces.length === 0) return outcome;
  if (!keyId) {
    outcome.missingKeyId = true;
    return outcome;
  }
  outcome.requested = true;
  for (const workspace of workspaces) {
    try {
      await allowKey(workspace.workspace_id, keyId);
      outcome.allowed += 1;
    } catch {
      outcome.failed += 1;
    }
  }
  return outcome;
}

/** The pre-checked "allow my workspace" box on the Add-a-key form; nothing when there is no workspace. */
export function AllowOnStoreField({ workspaceCount, checked, onChange }) {
  if (!workspaceCount) return null;
  return (
    <label className="provider-key-workspace-toggle" htmlFor="provider-keys-allow">
      <input
        id="provider-keys-allow"
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {' '}Allow my workspace {ALLOW_PERMISSION_TEXT}. Requests it makes are charged to your own provider
      account. You can turn this off at any time.
    </label>
  );
}

/**
 * One switch per workspace: "Allow my workspace to use this key for chat and generation".
 * `access` is `[{ workspace, approval }]`, `approval` being null when not allowed.
 */
export const NO_WORKSPACE_KEY_HINT =
  'Once you set up your workspace, you can choose whether it may use this key.';

export function WorkspaceKeyAccess({ access, busy, onToggle }) {
  // F-23: say why the switch is missing instead of rendering nothing.
  if (!access || access.length === 0) {
    return <p className="provider-key-workspaces provider-key-no-workspace">{NO_WORKSPACE_KEY_HINT}</p>;
  }
  return (
    <div className="provider-key-workspaces">
      {access.map(({ workspace, approval }) => (
        <label key={workspace.workspace_id} className="provider-key-workspace-toggle">
          <input
            type="checkbox"
            role="switch"
            checked={Boolean(approval)}
            disabled={busy}
            onChange={() => onToggle(workspace, approval)}
          />
          {' '}Allow my workspace{access.length > 1 ? ` (${workspaceName(workspace)})` : ''} {ALLOW_PERMISSION_TEXT}
        </label>
      ))}
      <HelpTip topic="workspace access">
        Your hosted workspace (Visual Canvas, Audiovisual Canvas, Prompt Studio and the other
        studios) never holds your keys. Auteur makes each call for it, and only with the keys you
        allow here, for chat and for image, video and audio generation alike. Turn it off at any
        time; the next call is refused. A key you add is allowed by default (you can untick that
        when you add it); a key you replace keeps the setting it had.
      </HelpTip>
    </div>
  );
}

const MEDIA_UNIT_LABELS = {
  image: 'images', video_second: 'video seconds', audio_character: 'audio characters',
  audio_second: 'audio seconds', job: 'jobs',
};

export function parseMediaLimitDraft(draft) {
  if (typeof draft !== 'string') throw new Error('Enter a positive whole number, or leave blank for unlimited.');
  const text = draft.trim();
  if (!text) return null;
  if (!/^[0-9]+$/.test(text) || !Number.isSafeInteger(Number(text))
      || Number(text) < 1 || Number(text) > MEDIA_LIMIT_MAX_UNITS) {
    throw new Error('Enter a positive whole number up to 2,000,000,000, or leave blank for unlimited.');
  }
  return Number(text);
}

function matchingMediaOperations(limitable, provider, media) {
  const catalog = Array.isArray(media) ? media : [];
  return limitable.filter((row) => row.provider === provider && catalog.some(
    (spec) => spec && spec.operation === row.operation && spec.unit_kind === row.unit_kind,
  ));
}
/** One settings control per provider, regardless of how many keys the customer has stored. */
export function firstMediaProviderKey(records, record) {
  return records.find((item) => item.provider === record.provider)?.key_id === record.key_id;
}

/**
 * The mounted controls' request/state owner. Tests drive these same actions with stubbed fetch.
 * All mutations are explicit; loading and refresh only GET. Limits belong to workspace/provider/operation.
 */
export function createMediaControlsSession(workspaceId, provider, media, onChange) {
  let state = { limits: null, limitable: [], usage: null, busy: false, error: '', usageError: '', notice: '' };
  let disposed = false;
  const emit = (patch) => {
    if (disposed) return;
    state = { ...state, ...patch };
    onChange(state);
  };
  const operations = () => matchingMediaOperations(state.limitable, provider, media);
  async function readUsage() {
    try {
      const usage = await readWorkspaceMediaUsage(workspaceId);
      emit({ usage, usageError: '' });
    } catch {
      emit({ usageError: 'Usage is unavailable. Refresh to try again.' });
    }
  }
  return {
    getState: () => state,
    dispose: () => { disposed = true; },
    async continueFor(scope) {
      if (disposed || state.busy || !['user_limit', 'guard'].includes(scope)) return false;
      emit({ busy: true, notice: '' });
      try {
        await grantWorkspaceMediaContinue(workspaceId, provider, scope);
        emit({ notice: 'Continue is active for 15 minutes. No generation or retrieval was started. Return to your existing job to retrieve its result.' });
        if (!disposed) await readUsage();
        return true;
      } catch {
        emit({ notice: 'Continue could not be confirmed. Refresh before trying again; no media action was started.' });
        return false;
      } finally { emit({ busy: false }); }
    },
    async refresh() {
      if (disposed || state.busy) return false;
      emit({ busy: true, error: '', notice: '' });
      try {
        const result = await listWorkspaceMediaLimits(workspaceId);
        emit({ ...result, error: '' });
      } catch {
        // Preserve the last saved state, but disable writes until a successful refresh.
        emit({ error: 'Generation limits are unavailable. Refresh to try again.' });
      }
      if (!disposed) await readUsage();
      emit({ busy: false });
      return true;
    },
    async save(operation, draft) {
      if (disposed || state.busy || state.limits === null || state.error) return false;
      const spec = operations().find((row) => row.operation === operation);
      if (!spec) return false;
      let units;
      try { units = parseMediaLimitDraft(draft); }
      catch (error) { emit({ notice: error.message }); return false; }
      emit({ busy: true, notice: '' });
      try {
        let saved = null;
        if (units === null) await clearWorkspaceMediaLimit(workspaceId, provider, operation);
        else {
          saved = await setWorkspaceMediaLimit(workspaceId, provider, operation, units);
          if (saved.unit_kind !== spec.unit_kind) throw new Error('operation mismatch');
        }
        const limits = state.limits.filter((row) => !(row.provider === provider && row.operation === operation));
        if (saved) limits.push(saved);
        emit({ limits, notice: units === null ? 'Your optional limit is cleared.' : 'Your limit is saved.' });
        if (!disposed) await readUsage();
        return true;
      } catch {
        emit({ error: 'Limit could not be confirmed. Your previous setting remains shown; refresh before retrying.' });
        return false;
      } finally { emit({ busy: false }); }
    },
  };
}

export function MediaLimitField({ spec, saved, usage, usageError, usageLoaded, disabled, onSave, id }) {
  const savedUnits = saved?.limit_units ?? null;
  const [draft, setDraft] = useState(savedUnits === null ? '' : String(savedUnits));
  useEffect(() => { setDraft(savedUnits === null ? '' : String(savedUnits)); }, [savedUnits]);
  const units = MEDIA_UNIT_LABELS[spec.unit_kind];
  return (
    <div className="provider-key-media-operation">
      <form onSubmit={(event) => { event.preventDefault(); onSave(spec.operation, draft); }}>
        <label htmlFor={id}>{spec.operation}: optional limit ({units} in the last 24 hours)</label>
        <input id={id} type="text" inputMode="numeric" autoComplete="off" value={draft}
          disabled={disabled} onChange={(event) => setDraft(event.target.value)}
          aria-describedby={`${id}-saved`} />
        <button type="submit" disabled={disabled}>Save limit</button>
        <button type="button" disabled={disabled || savedUnits === null}
          onClick={() => onSave(spec.operation, '')}>Clear limit</button>
      </form>
      <p id={`${id}-saved`}>Saved: {savedUnits === null ? 'Unlimited' : `${savedUnits.toLocaleString()} ${units}`}.
        {' '}Leave blank and save to remove your limit.</p>
      <p role="status">
        {usageError ? usageError : !usageLoaded ? 'Usage is loading.' : usage
          ? `Used ${usage.used_units.toLocaleString()} ${units} in the last 24 hours.${usage.continue_live ? ' A temporary Continue grant is active.' : ''}`
          : 'No recorded usage in the last 24 hours.'}
      </p>
    </div>
  );
}

/** F-69: the studio's plain name for the limits drawer. Never the workspace id or host name. */
export function studioDisplayName(workspace) {
  const name = String((workspace && (workspace.display_name || workspace.name)) || '').trim();
  return name && !/^wsp[_-]/i.test(name) ? name : 'your studio';
}

export function MediaWorkspaceView({ workspace, provider, providerLabel, media, state, blocked, onSave, onRefresh, onContinue }) {
  const operations = matchingMediaOperations(state.limitable, provider, media);
  const providerName = providerLabel || provider;
  return (
    <div className="provider-key-workspaces">
      <h3>Spending limits and usage for {providerName} in {studioDisplayName(workspace)}</h3>
      <p>These settings apply to all your {providerName} keys in this studio. Limits are optional;
        {' '}blank means unlimited. Usage is informational.</p>
      <button type="button" disabled={blocked || state.busy} onClick={onRefresh}>Refresh limits and usage</button>
      <p>Continue temporarily lifts the selected pause for this provider. Key access uses the switch above.</p>
      <button type="button" disabled={blocked || state.busy} onClick={() => onContinue?.('user_limit')}>Continue past my limit for 15 minutes</button>
      <button type="button" disabled={blocked || state.busy} onClick={() => onContinue?.('guard')}>Continue past repeating-request pause for 15 minutes</button>
      {state.error ? <p role="alert">{state.error}</p> : null}
      {state.notice ? <p role="status">{state.notice}</p> : null}
      {state.limits === null ? <p role="status">Generation limits are {state.busy ? 'loading' : 'unavailable'}.</p>
        : operations.map((spec) => (
          <MediaLimitField key={spec.operation} spec={spec}
            id={`media-${workspace.workspace_id}-${provider}-${encodeURIComponent(spec.operation)}`}
            saved={state.limits.find((row) => row.provider === provider && row.operation === spec.operation)}
            usage={state.usage?.find((row) => row.provider === provider && row.operation === spec.operation && row.unit_kind === spec.unit_kind)}
            usageLoaded={state.usage !== null} usageError={state.usageError}
            disabled={blocked || state.busy || Boolean(state.error)} onSave={onSave} />
        ))}
    </div>
  );
}

function MediaWorkspaceControls({ workspace, providerInfo, blocked }) {
  const [state, setState] = useState({ limits: null, limitable: [], usage: null, busy: false, error: '', usageError: '', notice: '' });
  const session = useRef(null);
  const provider = providerInfo.id;
  const mediaSignature = JSON.stringify(providerInfo.media);
  useEffect(() => {
    const controls = createMediaControlsSession(workspace.workspace_id, provider, JSON.parse(mediaSignature), setState);
    session.current = controls;
    setState(controls.getState());
    controls.refresh();
    return () => { controls.dispose(); session.current = null; };
  }, [workspace.workspace_id, provider, mediaSignature]);
  const providerName = providerInfo.label || provider;
  // F-70: closed by default, one drawer per provider; the limits load as soon as the row mounts.
  return (
    <details className="provider-key-drawer">
      <summary>Spending limits and usage (optional)</summary>
      <MediaWorkspaceView workspace={workspace} provider={provider} providerLabel={providerName} media={providerInfo.media}
        state={state} blocked={blocked} onSave={(operation, draft) => session.current?.save(operation, draft)}
        onRefresh={() => session.current?.refresh()} onContinue={(scope) => session.current?.continueFor(scope)} />
    </details>
  );
}
/** The status light with its words; colour is never the only signal (F-27). */
export function StatusLight({ light, label, detail }) {
  return (
    <span className={`status-light status-light-${light}`}>
      <span className="status-light-dot" aria-hidden="true" />
      <span className="status-light-label">{label}</span>
      {detail ? <span className="status-light-detail"> {detail}</span> : null}
    </span>
  );
}

/** Why "Test this key" cannot run here, or '' when it can. */
export function checkUnavailableReason({ isNotAvailable, validatorEnabled, readinessProvider = null }) {
  if (isNotAvailable) return "This provider has no free check, so Auteur can't test this key automatically.";
  if (validatorEnabled === false) {
    // Only what this provider's built operations say: vision is the one route that needs a checked key.
    const operations = ((readinessProvider && readinessProvider.operations) || [])
      .filter((op) => op.adapter === 'implemented').map((op) => op.operation);
    const needsCheck = operations.includes('vision/describe');
    return "Checking isn't available on this deployment yet. Your key is stored." +
      (needsCheck ? ' Image analysis (vision) only runs with a key that has passed a check, so it is not available with this key yet.' : '');
  }
  return '';
}

export function ProviderKeyRow({ record, providerInfo, readinessProvider, validatorEnabled, busy, now, onReplace, onDelete, onValidate, access, onToggleWorkspace, showMedia, rowMessage }) {
  const [draft, setDraft] = useState('');
  const [replacing, setReplacing] = useState(false);
  const isNotAvailable = providerInfo?.verification === 'not_available';
  const statusLabel = isNotAvailable
    ? "Auteur can't check this key automatically"
    : record.validation_freshness === 'not_recently_checked' || (record.validation_status === 'valid' && !recentlyValidated(record, now))
      ? 'Not recently checked'
      : record.validation_status === 'valid'
        ? validAsOf(record)
        : record.validation_status === 'unvalidated' && validatorEnabled === false
          ? 'Not checked (automatic checks are off on this deployment)'
          : STATUS_LABELS[record.validation_status] || record.validation_status;
  const light = keyLight(record, { checksOff: validatorEnabled === false, cannotVerify: isNotAvailable, now });
  const checkBlocked = checkUnavailableReason({ isNotAvailable, validatorEnabled, readinessProvider });

  const submitReplacement = useCallback(
    (event) => {
      event.preventDefault();
      const secret = draft;
      // Cleared before the request is awaited, not after: an in-flight request
      // that never resolves must not leave the key sitting in component state.
      setDraft('');
      setReplacing(false);
      onReplace(record.key_id, secret);
    },
    [draft, onReplace, record.key_id],
  );

  return (
    <li className="provider-key-row" data-provider={record.provider}>
      <div className="provider-key-light">
        <StatusLight light={light.light} label={light.label} detail={light.detail} />
      </div>
      <dl className="provider-key-summary">
        <div><dt>Provider</dt><dd className="provider-key-provider">{providerInfo?.label || record.provider}</dd></div>
        <div><dt>Label</dt><dd className="provider-key-label">{record.label || '(no label)'}</dd></div>
        {/* masked_hint is the ONLY part of a key this app ever renders. */}
        <div><dt>Key</dt><dd><code className="provider-key-hint">{record.masked_hint}</code></dd></div>
        <div><dt>Status</dt><dd className="provider-key-status">{statusLabel}</dd></div>
        <div><dt>Version</dt><dd className="provider-key-version">v{record.version}</dd></div>
      </dl>

      <div className="provider-key-actions">
        <button type="button" disabled={busy || isNotAvailable || validatorEnabled === false}
          aria-describedby={checkBlocked ? `check-why-${record.key_id}` : undefined}
          onClick={() => onValidate(record.key_id)}>
          {isNotAvailable ? 'Automatic check unavailable' : 'Test this key (free check)'}
        </button>
        <button type="button" disabled={busy} onClick={() => setReplacing((open) => !open)}>
          {replacing ? 'Cancel replacing' : 'Replace key'}
        </button>
        <button type="button" disabled={busy} onClick={() => onDelete(record, providerInfo?.label || record.provider)}>
          Delete
        </button>
      </div>
      {checkBlocked ? <p className="provider-key-check-note" id={`check-why-${record.key_id}`}>{checkBlocked}</p> : null}
      {rowMessage ? <p className="provider-key-check-note" role="status">{rowMessage}</p> : null}

      <KeyOperations readinessProvider={readinessProvider} />
      {coverageLine(record.provider, providerInfo?.label || record.provider)
        ? <p className="provider-key-coverage">{coverageLine(record.provider, providerInfo?.label || record.provider)}</p> : null}

      <WorkspaceKeyAccess
        access={access}
        busy={busy}
        onToggle={(workspace, approval) => onToggleWorkspace(workspace, record, approval)}
      />

      {showMedia && Array.isArray(providerInfo?.media) && providerInfo.media.length > 0
        ? access.map(({ workspace }) => <MediaWorkspaceControls key={workspace.workspace_id}
            workspace={workspace} providerInfo={providerInfo} blocked={busy} />)
        : null}

      {replacing ? (
        <form className="provider-key-replace" onSubmit={submitReplacement}>
          <label htmlFor={`replace-${record.key_id}`}>New key</label>
          <input
            id={`replace-${record.key_id}`}
            name="secret"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          {!busy && !draft ? <p className="provider-key-check-note" id={`replace-why-${record.key_id}`}>Paste the new key above to replace this one.</p> : null}
          <button type="submit" disabled={busy || !draft} aria-describedby={!draft ? `replace-why-${record.key_id}` : undefined}>
            Replace
          </button>
        </form>
      ) : null}
    </li>
  );
}

/**
 * "Add a key", grouped by what each provider does (F-26). Providers come from the server catalog
 * only; each shows its plain purpose line. With `focusCapabilities` those groups come first and the
 * rest wait behind "Show all providers". Each group shows a summary light for the keys stored in it.
 */
/** F-70: Enter in the search box must never submit the key form. */
export function suppressEnterSubmit(event) {
  if (event.key === 'Enter') event.preventDefault();
}

/** F-58: bring the opened provider entry into view and move focus into it. Returns whether it existed. */
export function openEntryIntoView(doc) {
  const entry = doc && typeof doc.getElementById === 'function' ? doc.getElementById('provider-keys-entry') : null;
  if (!entry) return false;
  if (typeof entry.scrollIntoView === 'function') entry.scrollIntoView({ block: 'start', behavior: 'smooth' });
  if (typeof entry.focus === 'function') entry.focus({ preventScroll: true });
  return true;
}

/** The best light among one provider's stored keys, or null when it has none (F-70: status beside the name). */
export function providerLight(records, providerId, lightFor) {
  const lights = (records || []).filter((record) => record.provider === providerId).map(lightFor);
  if (lights.length === 0) return null;
  return lights.find((item) => item.light === 'green') || lights.find((item) => item.light === 'yellow') || lights[0];
}

export function CapabilityPicker({
  providers, provider, onChoose, focusCapabilities, records, lightFor, showAll, onShowAll, renderEntry,
  selectedGroup = '',
}) {
  const [query, setQuery] = useState('');
  // The capability row the customer clicked in, so the entry opens under THAT row (a provider is in several).
  const [clickedGroup, setClickedGroup] = useState(selectedGroup);
  const activeGroup = selectedGroup || clickedGroup;
  const searching = query.trim() !== '';
  const ordered = orderedCapabilities(focusCapabilities);
  const hidden = ordered.filter((group) => !group.focused);
  // A search looks across every group, even the ones behind "Show all providers".
  const holdsChosen = (group) => providersIn(providers, group.capability.id).some((item) => item.id === provider);
  const visible = ordered.filter((group) => group.focused || showAll || searching || holdsChosen(group));
  const shown = (item) => item.id === provider || matchesProviderSearch(item, query);
  // The open entry sits under the provider in the first group that lists it, so its ids stay unique.
  const holdsOpen = ({ capability }) => providersIn(providers, capability.id).some((item) => item.id === provider && shown(item));
  const entryGroup = visible.find((group) => group.capability.id === activeGroup && holdsOpen(group)) || visible.find(holdsOpen);
  return (
    <div className="provider-capabilities">
      <div className="provider-search">
        <label htmlFor="provider-keys-search">Find a provider</label>
        <input id="provider-keys-search" type="search" autoComplete="off" spellCheck={false} value={query}
          placeholder="For example OpenAI, voice or video"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={suppressEnterSubmit} />
      </div>
      {visible.map(({ capability }) => {
        const members = providersIn(providers, capability.id).filter(shown);
        if (members.length === 0) return null;
        const stored = (records || []).filter((record) => members.some((item) => item.id === record.provider));
        const summary = groupLight(stored.map(lightFor));
        return (
          <fieldset key={capability.id} className="provider-capability" data-capability={capability.id}>
            <legend>{capability.title}</legend>
            <p className="provider-capability-explanation">{capability.explanation}</p>
            <p className="provider-capability-summary">
              {summary ? <StatusLight light={summary.light} label={summary.label} /> : 'No key added here yet.'}
            </p>
            <ul className="provider-capability-providers">
              {members.map((item) => {
                const id = `provider-${capability.id}-${item.id}`;
                const guide = guideFor(item.id);
                const planned = isPlannedFor(item.id, capability.id);
                const status = providerLight(records, item.id, lightFor);
                const open = provider === item.id && Boolean(entryGroup) && entryGroup.capability.id === capability.id;
                return (
                  <li key={item.id} className={open ? 'provider-capability-open' : undefined}>
                    <input type="radio" id={id} name={`provider-${capability.id}`} value={item.id}
                      checked={open} onChange={() => { setClickedGroup(capability.id); onChoose(item.id, capability.id); }}
                      onClick={() => { if (open) onChoose(''); }} />
                    <label htmlFor={id}>
                      <strong>{item.label}</strong>
                      {status ? <span className="provider-capability-status"> <StatusLight light={status.light} label={status.label} /></span> : null}
                      {item.id === RECOMMENDED_PROVIDER ? <span className="provider-capability-badge"> Recommended to start</span> : null}
                      {planned ? <span className="provider-capability-badge provider-capability-planned"> Planned for this use</span> : null}
                      {guide ? <span className="provider-capability-purpose">{guide.powers}</span> : null}
                    </label>
                    {open && entryGroup && entryGroup.capability.id === capability.id && renderEntry ? (
                      <div className="provider-entry" id="provider-keys-entry" tabIndex={-1}
                        role="group" aria-label={`Add a key for ${item.label}`}>
                        {renderEntry(item)}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
      {searching && !visible.some(({ capability }) => providersIn(providers, capability.id).some(shown)) ? (
        <p role="status">No provider matches &quot;{query.trim()}&quot;.</p>
      ) : null}
      {hidden.length > 0 && !searching ? (
        <button type="button" className="secondary" aria-expanded={showAll} onClick={() => onShowAll(!showAll)}>
          {showAll ? 'Show fewer providers' : 'Show all providers'}
        </button>
      ) : null}
    </div>
  );
}

/** F-15 / F-21: the warm "bring your own providers" explanation, shown before anything technical. */
export function BringYourOwnIntro({ onChooseRecommended, recommendedAvailable }) {
  return (
    <div className="provider-keys-byo">
      <p className="provider-keys-intro">
        <strong>Your tools, your choice.</strong> Auteur works with the AI providers you already use. Connect
        any of them with your own key: you pay them directly, and we never mark up your usage. Requests you
        run draw on your keys and on nobody else&apos;s.
      </p>
      <p>{GUIDE_TEXT.whatIsAKey}</p>
      <p>Creating a key is free. You only pay the provider when you generate something, at their own prices.</p>
      <p className="provider-keys-recommend">
        <strong>Not sure where to start?</strong> Start with OpenRouter: one free account and one key that
        reaches many AI models for writing and chat.
        {recommendedAvailable && onChooseRecommended ? (
          <>{' '}<button type="button" className="secondary" onClick={onChooseRecommended}>Use OpenRouter</button></>
        ) : null}
      </p>
    </div>
  );
}

/** F-24: what to do after a key is stored. Checking it is optional. */
export function StoredNextSteps({ onAddAnother, onDone }) {
  return (
    <div className="provider-keys-next" role="group" aria-label="Key stored: next steps">
      <p>Your key is saved. Testing it is a free check (&quot;Test this key&quot; beside it). It is optional for
        writing and generation; image analysis (vision) only runs with a key that has passed a check.</p>
      <button type="button" onClick={onAddAnother}>Add another key</button>
      <button type="button" className="secondary" onClick={onDone}>Done</button>
    </div>
  );
}

export default function ProviderKeysPage() {
  return <ProviderKeysPanel />;
}

/**
 * `focusCapabilities` (optional): capability ids to show first, e.g. from the onboarding
 * questionnaire; the other groups stay one click away. `onDone` (optional): called by the
 * "Done" button after a key is stored (onboarding moves on); Settings just closes the prompt.
 */
export function ProviderKeysPanel({ focusCapabilities = null, onDone = null } = {}) {
  // A workspace's "How to unlock" hint links here with ?provider=<id>: preselect it and open its guide.
  const search = useSearch();
  const deepProvider = deepLinkId(search, 'provider');
  const deepApplied = useRef(false);
  const [records, setRecords] = useState(null);
  // The server's readiness projection (no workspace selected here: the per-workspace switches say that).
  const [readiness, setReadiness] = useState(null);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState('');
  const [providerGroup, setProviderGroup] = useState('');
  const [label, setLabel] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  // The Add-a-key box "Allow my workspace to use this key ..." (default on: the customer's visible choice).
  const [allowOnStore, setAllowOnStore] = useState(true);
  // [{ workspace, approvals: SpendApproval[] }]; empty when the org has no workspace or
  // this deployment hosts none (the routes answer 503): then no switch is shown.
  const [workspaceAccess, setWorkspaceAccess] = useState([]);
  const [showAll, setShowAll] = useState(false);
  // F-24: after a successful store, offer "Add another key" / "Done".
  const [justStored, setJustStored] = useState(false);
  // The result of "Test this key", shown on that key's row (the page notice can be scrolled away).
  const [rowMessage, setRowMessage] = useState(null);

  const onChooseProvider = useCallback((chosenProvider, chosenGroup = '') => {
    setProvider(chosenProvider);
    setProviderGroup(chosenGroup);
  }, []);

  const loadWorkspaceAccess = useCallback(async () => {
    try {
      const workspaces = await listWorkspaces();
      const rows = await Promise.all(
        workspaces.map(async (workspace) => ({ workspace, approvals: await listSpendApprovals(workspace.workspace_id) })),
      );
      setWorkspaceAccess(rows);
    } catch {
      setWorkspaceAccess([]);
    }
  }, []);

  useEffect(() => {
    loadWorkspaceAccess();
  }, [loadWorkspaceAccess]);

  const refresh = useCallback(async () => {
    try {
      const payload = await listProviderKeys();
      setNow(Date.now());
      setRecords((payload && payload.provider_keys) || []);
      setReadiness((payload && payload.readiness) || null);
      setError(null);
      const catalog = (payload && payload.providers) || [];
      setProviders(catalog);
      setProvider((current) => (
        current && catalog.some((item) => item.id === current)
          ? current
          : ''
      ));
    } catch (failure) {
      setRecords(null);
      setReadiness(null);
      setError(failure.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (deepApplied.current || !deepProvider || !providers.some((item) => item.id === deepProvider)) return;
    deepApplied.current = true;
    setProvider(deepProvider);
    setProviderGroup('');
    setShowAll(true);
  }, [deepProvider, providers]);

  // F-58: choosing a provider opens its guide and key entry right under it; bring that into view and
  // move focus there so the click visibly does something. Moving the same provider between capability
  // rows (PR #596 P2) re-runs scroll and focus, while user actions (mutations, typing, refresh) do not.
  useEffect(() => {
    if (!provider || typeof document === 'undefined') return;
    openEntryIntoView(document);
  }, [provider, providerGroup]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  /**
   * Run one vault mutation.
   *
   * The caller has already cleared its own draft state before calling, so the
   * key exists only as this function's argument and only until the request
   * settles. Nothing here logs `secret`, and the catch branch reports
   * `failure.message` — a canned string from the server's closed error
   * vocabulary — never the request body.
   */
  const run = useCallback(
    async (operation, successMessage) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      setJustStored(false);
      setRowMessage(null);
      try {
        const result = await operation();
        setNotice(typeof successMessage === 'function' ? successMessage(result) : successMessage);
        await refresh();
        return true;
      } catch (failure) {
        setError(failure.message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const submitNewKey = useCallback(
    (event) => {
      event.preventDefault();
      const secret = draft;
      const submittedLabel = label;
      const allow = allowOnStore;
      const workspaces = workspaceAccess.map(({ workspace }) => workspace);
      setDraft('');
      setLabel('');
      run(
        async () => {
          const outcome = await storeKeyAndAllow({
            store: () => storeProviderKey({ provider, label: submittedLabel, secret }),
            allowKey: allowWorkspaceKey,
            workspaces,
            allow,
          });
          if (outcome.requested) await loadWorkspaceAccess();
          return outcome;
        },
        storeNotice,
      ).then((ok) => { if (ok) setJustStored(true); });
    },
    [draft, label, provider, run, allowOnStore, workspaceAccess, loadWorkspaceAccess],
  );

  const onReplace = useCallback(
    (keyId, secret) =>
      run(
        () => replaceProviderKey(keyId, { secret }),
        'Key replaced. The previous value is gone.',
      ),
    [run],
  );

  const onDelete = useCallback(
    (record, providerLabel) => {
      const name = providerLabel + (record.label ? ' — ' + record.label : ' — unlabeled key');
      if (!window.confirm('Delete ' + name + '? The key will be destroyed immediately and cannot be recovered.')) return;
      run(
        async () => {
          const result = await deleteProviderKey(record.key_id);
          // IN-19: re-read the workspace switches too, so nothing shows the deleted key as allowed.
          await loadWorkspaceAccess();
          return result;
        },
        (result) =>
          `Key destroyed. Proof: ${(result && result.deleted && result.deleted.revocation_id) || 'recorded'}.`,
      );
    },
    [run, loadWorkspaceAccess],
  );

  const onToggleWorkspace = useCallback(
    (workspace, record, approval) =>
      run(
        async () => {
          if (approval) await stopWorkspaceKey(workspace.workspace_id, approval.approval_id);
          else await allowWorkspaceKey(workspace.workspace_id, record.key_id);
          await loadWorkspaceAccess();
        },
        approval
          ? 'Your workspace can no longer use this key.'
          : 'Your workspace can now use this key.',
      ),
    [run, loadWorkspaceAccess],
  );

  const onValidate = useCallback(
    (keyId) =>
      run(
        () => validateProviderKey(keyId),
        (result) => {
          const message = result.validated
            ? 'Checked with the provider. The light beside the key shows the result.'
            : "Checking isn't available on this deployment yet; no provider call was made. Your key is still stored.";
          setRowMessage({ keyId, text: message });
          return message;
        },
      ),
    [run],
  );

  const addAnother = useCallback(() => {
    setJustStored(false);
    setNotice(null);
    const field = typeof document !== 'undefined' ? document.getElementById('provider-keys-add') : null;
    if (field && typeof field.scrollIntoView === 'function') field.scrollIntoView({ block: 'start' });
  }, []);

  const done = useCallback(() => {
    setJustStored(false);
    if (onDone) onDone();
  }, [onDone]);

  const validatorEnabled = readiness ? Boolean(readiness.validator?.enabled) : null;
  const lightFor = useCallback((record) => keyLight(record, {
    providerId: record.provider,
    checksOff: validatorEnabled === false,
    cannotVerify: providers.find((item) => item.id === record.provider)?.verification === 'not_available',
    now,
  }), [validatorEnabled, providers, now]);

  return (
    <section className="provider-keys-page">
      <header>
        <h1>Provider keys</h1>
        <BringYourOwnIntro
          recommendedAvailable={providers.some((item) => item.id === RECOMMENDED_PROVIDER)}
          onChooseRecommended={() => onChooseProvider(RECOMMENDED_PROVIDER, '')}
        />
      </header>

      <ReturnToWorkspace search={search} />
      {error ? <p className="provider-keys-error" role="alert">{error}</p> : null}
      {notice ? <p className="provider-keys-notice" role="status">{notice}</p> : null}
      {justStored ? <StoredNextSteps onAddAnother={addAnother} onDone={done} /> : null}

      <form className="provider-keys-form" id="provider-keys-add" onSubmit={submitNewKey}>
        <h2>Add a key</h2>

        <p>Pick what you want to do, then the provider you have (or want) an account with.</p>
        <CapabilityPicker
          providers={providers}
          provider={provider}
          selectedGroup={providerGroup}
          onChoose={onChooseProvider}
          focusCapabilities={focusCapabilities}
          records={records}
          lightFor={lightFor}
          showAll={showAll}
          onShowAll={setShowAll}
          renderEntry={(item) => (
            <>
              <p className="provider-keys-chosen" role="status">
                Adding a key for: <strong>{item.label || item.id}</strong>
              </p>
              {coverageLine(item.id, item.label || item.id)
                ? <p className="provider-key-coverage">{coverageLine(item.id, item.label || item.id)}</p> : null}
              {item.verification === 'not_available' ? (
                <p className="provider-keys-note">Auteur can&apos;t check this key automatically.</p>
              ) : null}
              {/* Step-by-step help for this provider; text lives in providerSetupGuides.ts. */}
              <ProviderSetupGuide
                providerId={provider}
                defaultOpen={Boolean(deepProvider) && provider === deepProvider}
                context="keys"
                verification={item.verification || null}
              />

              <label htmlFor="provider-keys-label">Label (optional)</label>
              <input
                id="provider-keys-label"
                type="text"
                value={label}
                maxLength={128}
                onChange={(event) => setLabel(event.target.value)}
              />

              <label htmlFor="provider-keys-secret">Key</label>
              <input
                id="provider-keys-secret"
                name="secret"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />

              {/* Rule EX-2: disclosed at the point of entry, beside the field. */}
              <p className="provider-keys-exclusion-notice">{VAULT_EXCLUSION_NOTICE}</p>

              <AllowOnStoreField
                workspaceCount={workspaceAccess.length}
                checked={allowOnStore}
                onChange={setAllowOnStore}
              />

              {!busy && (!draft || !provider) ? (
                <p className="provider-key-check-note" id="store-key-why">
                  {!provider ? 'Choose a provider, then paste your key to store it.' : 'Paste your key above to store it.'}
                </p>
              ) : null}
              <button type="submit" disabled={busy || !draft || !provider}
                aria-describedby={!draft || !provider ? 'store-key-why' : undefined}>
                Store key
              </button>
            </>
          )}
        />
        {!provider ? <p className="provider-keys-chosen" role="status">Click a provider above to add its key.</p> : null}
      </form>

      <h2>Stored keys</h2>
      {records === null ? (
        <p role="status">Provider key status is unavailable. Refresh this page to try again.</p>
      ) : records.length === 0 ? (
        <p className="provider-keys-empty">No provider keys stored yet.</p>
      ) : (
        <ul className="provider-keys-list">
          {records.map((record) => (
            <ProviderKeyRow
              key={record.key_id}
              record={record}
              showMedia={firstMediaProviderKey(records, record)}
              providerInfo={providers.find((item) => item.id === record.provider)}
              readinessProvider={readiness?.providers?.find((item) => item.id === record.provider) || null}
              validatorEnabled={validatorEnabled}
              rowMessage={rowMessage && rowMessage.keyId === record.key_id ? rowMessage.text : null}
              busy={busy}
              now={now}
              onReplace={onReplace}
              onDelete={onDelete}
              onValidate={onValidate}
              access={workspaceAccess.map(({ workspace, approvals }) => ({
                workspace,
                approval: approvals.find((item) => item.key_id === record.key_id) || null,
              }))}
              onToggleWorkspace={onToggleWorkspace}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
